import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "La Collina",
        metaTitle: "Ristorante La Collina | Carne e porceddu sardo",
        metaDescription: "Piatti di carne, porceddu sardo e vista sulle colline. Prenota il tuo tavolo in pochi secondi.",
        intro: "Cucina di collina, carni selezionate e una vista verde sulle montagne. Un posto caldo e accogliente per l’estate.",
        menu: [
            { label: "Degustazioni", href: "#degustazioni" },
            { label: "Specialità", href: "#specialita" },
            { label: "Prenota", href: "#prenota" }
        ],
        ctaPrimary: "Prenota un tavolo",
        ctaSecondary: "Le specialità",
        viewTitle: "Vista sulle colline",
        viewSubtitle: "Terrazza e sala con camino",
        tipLabel: "Consiglio",
        tipBody: "Prenota per il tramonto: la luce sulle colline è unica.",
        tastingsTitle: "Degustazioni di carne",
        tastings: [
            { title: "Menù del pastore", body: "Tre portate con carni sarde e contorni rustici." },
            { title: "Griglia di collina", body: "Carni selezionate e verdure di stagione." },
            { title: "Selezione di vini rossi", body: "Abbinamenti con cantine locali." }
        ],
        specialTitle: "Specialità della casa",
        specialItems: [
            { title: "Porceddu sardo", body: "Cottura lenta e crosta dorata." },
            { title: "Costine alla brace", body: "Marinatura alle erbe di montagna." },
            { title: "Tagliata di manzo", body: "Servita con sale al mirto." }
        ],
        bookTitle: "Prenota il tuo tavolo",
        bookSubtitle: "Un messaggio e ti rispondiamo subito.",
        bookCta1: "Prenota ora",
        bookCta2: "Chiama"
    },
    en: {
        title: "La Collina",
        metaTitle: "La Collina | Meat dishes and Sardinian porceddu",
        metaDescription: "Meat dishes, Sardinian porceddu and hillside views. Book your table in seconds.",
        intro: "Hillside cooking, selected meats and a green mountain view. A warm, welcoming place for summer.",
        menu: [
            { label: "Tastings", href: "#degustazioni" },
            { label: "Specialties", href: "#specialita" },
            { label: "Book", href: "#prenota" }
        ],
        ctaPrimary: "Book a table",
        ctaSecondary: "See specialties",
        viewTitle: "Hillside view",
        viewSubtitle: "Terrace and fireplace room",
        tipLabel: "Tip",
        tipBody: "Book at sunset: the light over the hills is special.",
        tastingsTitle: "Meat tastings",
        tastings: [
            { title: "Shepherd’s menu", body: "Three courses with Sardinian meats and rustic sides." },
            { title: "Hill grill", body: "Selected meats and seasonal vegetables." },
            { title: "Red wine selection", body: "Pairings with local wineries." }
        ],
        specialTitle: "House specialties",
        specialItems: [
            { title: "Sardinian porceddu", body: "Slow-cooked with golden crust." },
            { title: "Charcoal ribs", body: "Herb marinade from the hills." },
            { title: "Beef tagliata", body: "Served with myrtle salt." }
        ],
        bookTitle: "Book your table",
        bookSubtitle: "Send a message and we reply quickly.",
        bookCta1: "Book now",
        bookCta2: "Call"
    },
    de: {
        title: "La Collina",
        metaTitle: "La Collina | Fleischgerichte und sardischer Porceddu",
        metaDescription: "Fleischgerichte, sardischer Porceddu und Blick auf die Hügel. Tisch in Sekunden reservieren.",
        intro: "Hügelküche, ausgewählte Fleischgerichte und ein grüner Bergblick. Ein warmer Ort für den Sommer.",
        menu: [
            { label: "Degustation", href: "#degustazioni" },
            { label: "Spezialitäten", href: "#specialita" },
            { label: "Buchen", href: "#prenota" }
        ],
        ctaPrimary: "Tisch reservieren",
        ctaSecondary: "Spezialitäten",
        viewTitle: "Blick auf die Hügel",
        viewSubtitle: "Terrasse und Kaminzimmer",
        tipLabel: "Tipp",
        tipBody: "Zum Sonnenuntergang reservieren: das Licht ist besonders schön.",
        tastingsTitle: "Fleisch‑Degustationen",
        tastings: [
            { title: "Hirten‑Menü", body: "Drei Gänge mit sardischen Fleischgerichten." },
            { title: "Grill aus der Hügellandschaft", body: "Ausgewähltes Fleisch und Saison‑Gemüse." },
            { title: "Rotwein‑Auswahl", body: "Pairings mit lokalen Weingütern." }
        ],
        specialTitle: "Hausspezialitäten",
        specialItems: [
            { title: "Sardischer Porceddu", body: "Langsam gegart mit knuspriger Kruste." },
            { title: "Rippchen vom Grill", body: "Kräuter‑Marinade aus den Bergen." },
            { title: "Tagliata vom Rind", body: "Mit Myrten‑Salz serviert." }
        ],
        bookTitle: "Tisch reservieren",
        bookSubtitle: "Eine Nachricht genügt, wir antworten schnell.",
        bookCta1: "Jetzt buchen",
        bookCta2: "Anrufen"
    }
};

const RistoranteCollinaPage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/ristorante-collina"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-amber-50 via-lime-50 to-emerald-100 text-emerald-950">
                <header className="px-6 pt-10 pb-10 md:px-12 lg:px-20">
                    <ExampleLandingHeader title={t.title} items={t.menu} />
                    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1fr]">
                        <div>
                            <p className="text-lg text-emerald-800">{t.intro}</p>
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#prenota" className="rounded-full bg-emerald-800 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200">
                                    {t.ctaPrimary}
                                </a>
                                <a href="#specialita" className="rounded-full border border-emerald-700 px-6 py-3 text-sm font-semibold text-emerald-800">
                                    {t.ctaSecondary}
                                </a>
                            </div>
                        </div>
                        <div id="vista" className="rounded-3xl bg-white p-6 shadow-lg">
                            <h2 className="text-xl font-semibold">{t.viewTitle}</h2>
                            <p className="mt-2 text-sm text-emerald-700">{t.viewSubtitle}</p>
                            <div className="mt-6 rounded-2xl bg-lime-50 p-4">
                                <p className="text-sm uppercase tracking-widest text-emerald-700">{t.tipLabel}</p>
                                <p className="mt-2 text-sm">{t.tipBody}</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 overflow-hidden rounded-3xl shadow-lg">
                        <img
                            src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1600&auto=format&fit=crop"
                            alt="Terrazza con vista sulle colline"
                            className="h-64 w-full object-cover md:h-96"
                            loading="lazy"
                        />
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="degustazioni" className="rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.tastingsTitle}</h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {t.tastings.map((item) => (
                                <div key={item.title} className="rounded-2xl bg-emerald-50 p-4">
                                    <p className="font-semibold">{item.title}</p>
                                    <p className="mt-2 text-sm text-emerald-700">{item.body}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                    <section id="specialita" className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                        <div className="rounded-3xl bg-white p-6 shadow-lg md:p-10">
                            <h2 className="text-2xl font-semibold">{t.specialTitle}</h2>
                            <div className="mt-6 grid gap-4">
                                {t.specialItems.map((item) => (
                                    <div key={item.title} className="rounded-2xl bg-amber-50 p-4">
                                        <p className="font-semibold">{item.title}</p>
                                        <p className="mt-2 text-sm text-emerald-700">{item.body}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-3xl shadow-lg">
                            <img
                                src="https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=1400&auto=format&fit=crop"
                                alt="Porceddu sardo e piatti di carne"
                                className="h-64 w-full object-cover md:h-full"
                                loading="lazy"
                            />
                        </div>
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1400&auto=format&fit=crop"
                            alt="Piatto di carne alla griglia"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1454944338482-a69bb95894af?q=80&w=1400&auto=format&fit=crop"
                            alt="Taglio di carne con contorni"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <SocialLinksSection className="mt-10" />

                    <section id="prenota" className="mt-10 rounded-3xl bg-emerald-900 p-6 text-white shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.bookTitle}</h2>
                        <p className="mt-2 text-emerald-100">{t.bookSubtitle}</p>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href="https://wa.me/393515699197" className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-emerald-900">
                                {t.bookCta1}
                            </a>
                            <a href="tel:+393515699197" className="rounded-full border border-white px-6 py-3 text-sm font-semibold text-white">
                                {t.bookCta2}
                            </a>
                        </div>
                    </section>
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default RistoranteCollinaPage;
